import { ClipboardModule } from '@angular/cdk/clipboard';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { MissingTranslationHandler, TranslateModule } from '@ngx-translate/core';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NgxMaskDirective } from 'ngx-mask';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { QuillModule } from 'ngx-quill';
import {
  CommonInputComponent,
  CreateFolderComponent,
  DraggingDirective,
  DraggingHandleDirective,
  FILE_MANAGER_SERVICE_TOKEN,
  FileManagerDataServiceImpl,
  FOLDER_SERVICE_TOKEN,
  FolderService,
  GET_ASSETS_PATH_TOKEN,
  ImgComponent,
  LOCALIZED_TOASTER_SERVICE_TOKEN,
  MODAL_SERVICE_TOKEN,
  ModalComponent,
  TextEditorFileUploadModalComponent,
  TRANSLATE_SERVICE_TOKEN,
  UploadFileModalsHolderComponent,
  UploadFilesListingModalComponent,
  URL_SERVICE_TOKEN
} from 'shared-components';
import { ButtonsModule } from 'ngx-bootstrap/buttons';

import { UrlService } from 'app/core/http/url.service';
import { CustomMissingTranslationHandler } from 'app/core/internationalization/custom-missing-translation-handler';
import { TranslationService } from 'app/core/internationalization/translation.service';
import { getAssetsPath } from 'app/core/kh-utils/kh-utils';
import { ModalService } from 'app/core/modals/services/modal.service';
import { LocalizedToasterService } from 'app/core/toaster/localized-toaster.service';
import { RegisterRoleSelectorComponent } from 'app/register/components/register-role-selector/register-role-selector.component';
import { CountdownComponent } from 'app/shared/components/countdown/countdown.component';
import { SubscriptionCardComponent } from 'app/shared/components/subscription-card/subscription-card.component';
import { TranslateWithFormatDirective } from 'app/shared/directives/translate-with-format.directive';
import { HelpCenterComponent } from 'app/shared/help-center/help-center.component';
import { StudentBtnComponent } from 'app/student/components/student-btn/student-btn.component';
import { RewardCardComponent } from 'app/student/game-section/components/reward-card/reward-card.component';
import { InnerHtmlContainerComponent } from 'app/teacher/teacher-shared/components/inner-html-container/inner-html-container.component';
import { AdminPasswordInputComponent } from 'app/shared/components/admin-password-input/admin-password-input.component';

import { AlertComponent } from './components/alert/alert.component';
import { AvatarComponent } from './components/avatar/avatar.component';
import { BadgeDotComponent } from './components/badge-dot/badge-dot.component';
import { BannerComponent } from './components/banner/banner.component';
import { BreadcrumbsComponent } from './components/breadcrumbs/breadcrumbs.component';
import { BreadcrumbsDropdownComponent } from './components/breadcrumbs-dropdown/breadcrumbs-dropdown.component';
import { ButtonRadioGroupComponent } from 'app/shared/components/button-radio-group/button-radio-group.component';
import { ButtonToggleComponent } from 'app/shared/components/button-toggle/button-toggle.component';
import { CancelSubscriptionModalComponent } from './components/cancel-subscription-modal/cancel-subscription-modal.component';
import { CardComponent } from './components/card/card.component';
import { CarouselComponent } from './components/carousel/carousel.component';
import { ChangePasswordInputComponent } from './components/change-password-input/change-password-input.component';
import { ChatComponent } from './components/chat/chat.component';
import { CommonCheckboxComponent } from './components/common-checkbox/common-checkbox.component';
import { CommonDropdownComponent } from './components/common-dropdown/common-dropdown.component';
import { ContentBlocksComponent } from './components/content-blocks/content-blocks.component';
import { ContentSwitchComponent } from './components/content-switch/content-switch.component';
import { DateTimeSelectorComponent } from './components/date-time-selector/date-time-selector.component';
import { DistrictFormComponent } from './components/district-form/district-form.component';
import { DividerComponent } from './components/divider/divider.component';
import { DropdownComponent } from 'app/shared/components/dropdown/dropdown.component';
import { DropdownMenuComponent } from './components/dropdown-menu/dropdown-menu.component';
import { DropdownMenuItemComponent } from './components/dropdown-menu-item/dropdown-menu-item.component';
import { EmailNotificationsModalComponent } from './components/email-notifications-modal/email-notifications-modal.component';
import { ExploreActivitiesBlockComponent } from './components/explore-activities-block/explore-activities-block.component';
import { FixedSizeCarouselComponent } from './components/fixed-size-carousel/fixed-size-carousel.component';
import { FixedSizeCarouselDotComponent } from './components/fixed-size-carousel/fixed-size-carousel-dot/fixed-size-carousel-dot.component';
import { ForbiddenComponent } from './components/forbidden/forbidden.component';
import { FullScreenBtnComponent } from './components/full-screen-btn/full-screen-btn.component';
import { GoogleButtonComponent } from './components/google-button/google-button.component';
import { HomeworkLoaderComponent } from './components/homework-loader/homework-loader.component';
import { InfoBoxComponent } from './components/info-box/info-box.component';
import { InputComponent } from './components/input/input.component';
import { InputTextEditorComponent } from './components/input-text-editor/input-text-editor.component';
import { LabeledFieldComponent } from './components/labeled-field/labeled-field.component';
import { LimitedTextEditorComponent } from './components/limited-text-editor/limited-text-editor.component';
import { BackButtonComponent } from './components/back-button/back-button.component';
import { LoadingStatusComponent } from './components/loading-status/loading-status.component';
import { LoginTopBarComponent } from './components/login-top-bar/login-top-bar.component';
import { MergeAccountConfirmationComponent } from './components/merge-account-confirmation/merge-account-confirmation.component';
import { MergeAccountPreviewComponent } from './components/merge-account-preview/merge-account-preview.component';
import { MergeCompleteComponent } from './components/merge-complete/merge-complete.component';
import { MergeTeacherFormComponent } from './components/merge-teacher-form/merge-teacher-form.component';
import { MergeTeacherSendEmailComponent } from './components/merge-teacher-send-email/merge-teacher-send-email.component';
import { NewsWidgetComponent } from './components/news-widget/news-widget.component';
import { NewsWidgetItemComponent } from './components/news-widget-item/news-widget-item.component';
import { NewsWidgetItemTeacherComponent } from './components/news-widget-item-teacher/news-widget-item-teacher.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { PasswordInputComponent } from './components/password-input/password-input.component';
import { PasswordRequirementsComponent } from './components/password-requirements/password-requirements.component';
import { PopoverComponent } from './components/popover/popover.component';
import { PrimaryDropdownComponent } from './components/primary-dropdown/primary-dropdown.component';
import { RadioComponent } from './components/radio/radio.component';
import { RecentActivityContentComponent } from './components/recent-activity-content/recent-activity-content.component';
import { ReportsTopGapComponentComponent } from './components/reports-top-gap-component/reports-top-gap-component.component';
import { RolesSwitcherComponent } from './components/roles-switcher/roles-switcher.component';
import { RtcListenerComponent } from './components/rtc-listener/rtc-listener.component';
import { ScrollIndicatorComponent } from './components/scroll-indicator/scroll-indicator.component';
import { SearchFieldComponent } from './components/search-field/search-field.component';
import { SettingsViewComponent } from './components/settings/settings-view/settings-view.component';
import { SettingsLinkComponent } from './components/settings-link/settings-link.component';
import { SettingsMenuComponent } from './components/settings-menu/settings-menu.component';
import { SettingsToggleFieldComponent } from './components/settings-toggle-field/settings-toggle-field.component';
import { SettingsValueComponent } from './components/settings-value/settings-value.component';
import { SSOProviderButtonComponent } from './components/sso-provider-button/sso-provider-button.component';
import { StarRatingComponent } from './components/star-rating/star-rating.component';
import { StarRatingMaxLabelDirective } from './components/star-rating/star-rating-max-label.directive';
import { StarRatingMinLabelDirective } from './components/star-rating/star-rating-min-label.directive';
import { StudentCommonDropdownComponent } from './components/student-common-dropdown/student-common-dropdown.component';
import { StudentGapBoxComponent } from './components/student-gap-box/student-gap-box.component';
import { SvgComponent } from './components/svg/svg.component';
import { SwitchComponent } from './components/switch/switch.component';
import { KhTableComponent } from './components/table/table.component';
import { TeacherDeleteAccountModalComponent } from './components/teacher-delete-account-modal/teacher-delete-account-modal.component';
import { TopNavbarComponent } from './components/top-navbar/top-navbar.component';
import { UploadImageComponent } from './components/upload-image/upload-image.component';
import { VerifyEmailComponent } from './components/verify-email/verify-email.component';
import { VideoComponent } from './components/video/video.component';
import { VideoWithEventsComponent } from './components/video/video-with-events/video-with-events.component';
import { VideoPlayerComponent } from './components/video-player/video-player.component';
import { ViewInArIconComponent } from './components/view-in-ar-icon/view-in-ar-icon.component';
import { WebglUnsupportedComponent } from './components/webgl-unsupported/webgl-unsupported.component';
import { IfHasRoleDirective } from './directives';
import { ClickOutsideDirective } from './directives/click-outside/click-outside.directive';
import { RemoveIfFeatureOffDirective } from './directives/feature-flag/remove-if-feature-off.directive';
import { RemoveIfFeatureOnDirective } from './directives/feature-flag/remove-if-feature-on.directive';
import { IntersectionDirective } from './directives/intersection/intersection.directive';
import { DemoMessageModalComponent } from './modals/demo-message-modal/demo-message-modal.component';
import {
  ExploreActivitiesPickClassModalComponent
} from './modals/explore-activities-pick-class-modal/explore-activities-pick-class-modal.component';
import { GenericPickClassModalComponent } from './modals/generic-pick-class-modal/generic-pick-class-modal.component';
import { NewsArticleModalComponent } from './modals/news-article-modal/news-article-modal.component';
import { NewsArticleTeacherModalComponent } from './modals/news-article-modal-teacher/news-article-modal-teacher.component';
import { RemediationModalComponent } from './modals/remediation-modal/remediation-modal.component';
import { SelectUploadSourceComponent } from './modals/select-upload-source/select-upload-source.component';
import { TeacherRemediationModalComponent } from './modals/teacher-remediation-modal/teacher-remediation-modal.component';
import { UpsellModalComponent } from './modals/upsell-modal/upsell-modal.component';
import { WatchVideoComponent } from './modals/watch-video/watch-video.component';
import { DurationPipe } from './pipes/duration.pipe';
import { KhLocalizedDatePipe } from './pipes/kh-localized-date.pipe';
import { KhSplitClassCodePipe } from './pipes/kh-split-class-code.pipe';
import { LimitToPipe } from './pipes/limit-to.pipe';
import { OrderByPipe } from './pipes/order-by.pipe';
import { UnsafeHtmlPipe } from './pipes/unsafe-html.pipe';
import { UnsafeUrlPipe } from './pipes/unsafe-url.pipe';
import { TranslationLoaderHelper } from 'app/core/internationalization/translation-loader-helper';
import { BaseTranslatableModule } from 'app/core/internationalization/translatable-module';

const directives = [
  ClickOutsideDirective,
  IfHasRoleDirective,
  IntersectionDirective,
  RemoveIfFeatureOffDirective,
  RemoveIfFeatureOnDirective,
  StarRatingMaxLabelDirective,
  StarRatingMinLabelDirective
];

const pipes = [
  KhLocalizedDatePipe,
  LimitToPipe,
  OrderByPipe,
  UnsafeUrlPipe
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const modals: any[] = [
  CancelSubscriptionModalComponent,
  CreateFolderComponent,
  DemoMessageModalComponent,
  EmailNotificationsModalComponent,
  ModalComponent,
  NewsArticleModalComponent,
  NewsArticleTeacherModalComponent,
  RemediationModalComponent,
  SelectUploadSourceComponent,
  TeacherRemediationModalComponent,
  TextEditorFileUploadModalComponent,
  UploadFileModalsHolderComponent,
  UploadFilesListingModalComponent,
  UpsellModalComponent
];

const components = [
  AdminPasswordInputComponent,
  AlertComponent,
  BadgeDotComponent,
  BannerComponent,
  BreadcrumbsComponent,
  BreadcrumbsDropdownComponent,
  ButtonRadioGroupComponent,
  ButtonToggleComponent,
  CardComponent,
  CarouselComponent,
  ChangePasswordInputComponent,
  ChatComponent,
  CommonCheckboxComponent,
  CommonDropdownComponent,
  ContentBlocksComponent,
  ContentSwitchComponent,
  CountdownComponent,
  DateTimeSelectorComponent,
  DistrictFormComponent,
  DividerComponent,
  DropdownComponent,
  DropdownMenuComponent,
  DropdownMenuItemComponent,
  ExploreActivitiesBlockComponent,
  ExploreActivitiesPickClassModalComponent,
  FixedSizeCarouselComponent,
  FixedSizeCarouselDotComponent,
  ForbiddenComponent,
  GenericPickClassModalComponent,
  GoogleButtonComponent,
  HelpCenterComponent,
  HomeworkLoaderComponent,
  InfoBoxComponent,
  InnerHtmlContainerComponent,
  InputComponent,
  InputTextEditorComponent,
  KhTableComponent,
  LabeledFieldComponent,
  LimitedTextEditorComponent,
  LoadingStatusComponent,
  LoginTopBarComponent,
  MergeAccountConfirmationComponent,
  MergeAccountPreviewComponent,
  MergeCompleteComponent,
  MergeTeacherFormComponent,
  MergeTeacherSendEmailComponent,
  NewsWidgetComponent,
  NewsWidgetItemComponent,
  NewsWidgetItemTeacherComponent,
  PageNotFoundComponent,
  PasswordInputComponent,
  PasswordRequirementsComponent,
  PopoverComponent,
  PrimaryDropdownComponent,
  RadioComponent,
  RecentActivityContentComponent,
  RegisterRoleSelectorComponent,
  ReportsTopGapComponentComponent,
  RewardCardComponent,
  RolesSwitcherComponent,
  RtcListenerComponent,
  ScrollIndicatorComponent,
  SearchFieldComponent,
  SettingsLinkComponent,
  SettingsMenuComponent,
  SettingsToggleFieldComponent,
  SettingsValueComponent,
  SettingsViewComponent,
  SSOProviderButtonComponent,
  StarRatingComponent,
  StudentBtnComponent,
  StudentCommonDropdownComponent,
  StudentGapBoxComponent,
  SubscriptionCardComponent,
  SvgComponent,
  SwitchComponent,
  TeacherDeleteAccountModalComponent,
  TopNavbarComponent,
  UploadImageComponent,
  VerifyEmailComponent,
  VideoComponent,
  VideoPlayerComponent,
  VideoWithEventsComponent,
  WatchVideoComponent,
  WebglUnsupportedComponent
];

@NgModule({
  declarations: [
    ...directives,
    ...pipes,
    ...components,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    ...modals
  ],
  exports: [
    ClipboardModule,
    CollapseModule,
    DragDropModule,
    FontAwesomeModule,
    FormsModule,
    NgxMaskDirective,
    TooltipModule,
    TranslateModule,
    NgxSliderModule,
    DurationPipe,
    BackButtonComponent,
    ImgComponent,
    AvatarComponent,
    KhSplitClassCodePipe,
    FullScreenBtnComponent,
    UnsafeHtmlPipe,
    ...directives,
    CommonInputComponent,
    NgxMaskDirective,
    ...pipes,
    ...components,
    ViewInArIconComponent,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    ...modals,
    TranslateWithFormatDirective
  ],
  imports: [
    TypeaheadModule,
    BsDatepickerModule,
    CommonModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    RouterModule,
    CollapseModule,
    BsDropdownModule,
    AvatarComponent,
    BackButtonComponent,
    ImgComponent,
    UnsafeHtmlPipe,
    CommonInputComponent,
    NgxMaskDirective,
    TranslateWithFormatDirective,
    FullScreenBtnComponent,
    TranslateModule.forChild({
      extend: true,
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: CustomMissingTranslationHandler
      }
    }),
    TooltipModule,
    FormsModule,
    DragDropModule,
    PopoverModule,
    ClipboardModule,
    DragDropModule,
    ButtonsModule,
    AngularEditorModule,
    QuillModule,
    ModalModule,
    NgxSliderModule,
    CarouselModule,
    DurationPipe,
    KhSplitClassCodePipe,
    DraggingDirective,
    DraggingHandleDirective,
    ViewInArIconComponent
  ],
  providers: [
    DurationPipe,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { provide: GET_ASSETS_PATH_TOKEN, useValue: getAssetsPath },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { provide: MODAL_SERVICE_TOKEN, useExisting: ModalService },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { provide: FILE_MANAGER_SERVICE_TOKEN, useClass: FileManagerDataServiceImpl },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { provide: FOLDER_SERVICE_TOKEN, useClass: FolderService },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { provide: TRANSLATE_SERVICE_TOKEN, useExisting: TranslationService },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { provide: LOCALIZED_TOASTER_SERVICE_TOKEN, useExisting: LocalizedToasterService },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { provide: URL_SERVICE_TOKEN, useExisting: UrlService }
  ]
})
export class SharedModule extends BaseTranslatableModule {
  constructor(translationLoaderHelper: TranslationLoaderHelper) {
    super(translationLoaderHelper, 'shared');
  }
}
